import { gsap, Power2 } from 'gsap';
import { Transition } from '../Transition';
import { capitalizeFirstLetter } from '../helpers';
import '../types';

export class Slide extends Transition {
  /**
   * @typedef {options} slideOptions
   *  @property {"top"|"right"|"bottom"|"left"} [type=left] Type of animation
   */

  /**
   * @constructor
   * @class Slide
   * @classdesc Controls all Slide transitions (top, left, bottom, right)
   * @param {slideOptions} options
   */
  constructor(options) {
    super(options);

    const defaultOptions = {
      type: 'left',
    };

    this.options = { ...defaultOptions, ...options, ...this.options };
  }

  /**
   * Get animation properties by type
   * @param {string} type Type of animation
   * @returns {object} Animations properties
   */
  _getAnimationProperties(type) {
    type = capitalizeFirstLetter(type);
    return this[`_get${type}AnimationProperties`]();
  }

  /**
   * Get left animation properties
   * @returns {object} Animations properties
   */
  _getLeftAnimationProperties() {
    return {
      animateIn: {
        set: {
          transformOrigin: '0% 50%',
        },
        from: {
          z: 0,
          scaleX: 0,
        },
        to: {
          scaleX: 1,
        },
      },
      animateOut: {
        set: {
          transformOrigin: '100% 50%',
        },
        to: {
          scaleX: 0,
        },
      },
    };
  }

  /**
   * Get right animation properties
   * @returns {object} Animations properties
   */
  _getRightAnimationProperties() {
    return {
      animateIn: {
        set: {
          transformOrigin: '100% 50%',
        },
        from: {
          scaleX: 0,
        },
        to: {
          scaleX: 1,
        },
      },
      animateOut: {
        set: {
          transformOrigin: '0% 50%',
        },
        to: {
          scaleX: 0,
        },
      },
    };
  }

  /**
   * Get top animation properties
   * @returns {object} Animations properties
   */
  _getTopAnimationProperties() {
    return {
      animateIn: {
        set: {
          transformOrigin: '50% 0%',
        },
        from: {
          scaleY: 0,
        },
        to: {
          scaleY: 1,
        },
      },
      animateOut: {
        set: {
          transformOrigin: '50% 100%',
        },
        to: {
          scaleY: 0,
        },
      },
    };
  }

  /**
   * Get bottom animation properties
   * @returns {object} Animations properties
   */
  _getBottomAnimationProperties() {
    return {
      animateIn: {
        set: {
          transformOrigin: '50% 100%',
        },
        from: {
          scaleY: 0,
        },
        to: {
          scaleY: 1,
        },
      },
      animateOut: {
        set: {
          transformOrigin: '50% 0%',
        },
        to: {
          scaleY: 0,
        },
      },
    };
  }

  /**
   * Method for the animation "in"
   * @returns {Promise}
   */
  in() {
    return new Promise((resolve) => {
      const { type } = this.options;
      const duration = this._getOptionValue('duration');
      const {
        animateIn: { set, from, to },
      } = this._getAnimationProperties(type);
      const container = this.createTransitionContainer();
      const rect = document.createElement('div');
      const tl = new gsap.timeline({
        onComplete: () => {
          document.dispatchEvent(new Event('barbaAnimInEnd'));
          resolve();
        },
      });

      container.appendChild(rect);

      tl.set(rect, { attr: { class: 'wb-transition-slide' }, ...set });
      tl.fromTo(rect, { ...from }, { duration, ease: Power2.easeOut, ...to });
    });
  }

  /**
   * Method for the animation "out"
   * @returns {Promise}
   */
  out() {
    return new Promise((resolve) => {
      const { type } = this.options;
      const duration = this._getOptionValue('duration');
      const {
        animateOut: { set, to },
      } = this._getAnimationProperties(type);
      const rect = document.querySelector('.wb-transition-slide');
      const tl = new gsap.timeline({
        onComplete: () => {
          this.getTransitionContainer().remove();
          resolve();
        },
      });

      document.dispatchEvent(new Event('barbaAnimOutStart'));

      tl.set(rect, { ...set });
      tl.to(rect, { duration, ease: Power2.easeOut, ...to });
    });
  }
}
